// src/components/ManualBillingModal.jsx - FIXED: Portion Selection & Firebase Error

import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search, ShoppingCart, Loader } from 'lucide-react';

const ManualBillingModal = ({ 
  restaurantId, 
  menu = [], 
  tables = [],
  onClose, 
  onGenerateBill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [customerName, setCustomerName] = useState('Walk-in Customer');
  const [selectedTable, setSelectedTable] = useState('');
  const [manualItem, setManualItem] = useState({
    name: '',
    quantity: 1,
    price: ''
  });
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // ✅ NEW: Portion selection state
  const [showPortionModal, setShowPortionModal] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  // Filter menu items based on search
  const filteredMenu = menu.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    item.inStock !== false
  );

  // ✅ NEW: Handle menu item click - check if portion selection is needed
  const handleMenuItemClick = (menuItem) => {
    const hasHalf = menuItem.portionOptions?.includes('Half') && menuItem.priceHalf;
    const hasFull = menuItem.portionOptions?.includes('Full') && menuItem.priceFull;
    
    // If both portions available, show selection modal
    if (hasHalf && hasFull) {
      setSelectedMenuItem(menuItem);
      setShowPortionModal(true);
    } else {
      // Add directly with available portion
      const price = hasFull ? menuItem.priceFull : menuItem.priceHalf;
      const portion = hasFull ? 'Full' : 'Half';
      addItemFromMenu(menuItem, price, portion);
    }
  };

  // ✅ UPDATED: Add item from menu with portion
  const addItemFromMenu = (menuItem, price, portion) => {
    const itemKey = `${menuItem.id}_${portion}_${price}`;
    const existingIndex = selectedItems.findIndex(
      item => item.menuItemId === menuItem.id && item.price === price && item.portion === portion
    );

    if (existingIndex >= 0) {
      const updated = [...selectedItems];
      updated[existingIndex].quantity += 1;
      setSelectedItems(updated);
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          id: `item_${Date.now()}_${Math.random()}`,
          menuItemId: menuItem.id,
          name: `${menuItem.name} (${portion})`,
          price: price,
          quantity: 1,
          portion: portion,
          fromMenu: true
        }
      ]);
    }
    setSearchTerm('');
    setShowPortionModal(false);
    setSelectedMenuItem(null);
  };

  // Add manual item
  const addManualItem = () => {
    if (!manualItem.name || !manualItem.price || manualItem.quantity < 1) {
      alert('Please fill all fields for manual item');
      return;
    }

    setSelectedItems([
      ...selectedItems,
      {
        id: `manual_${Date.now()}_${Math.random()}`,
        name: manualItem.name,
        price: parseFloat(manualItem.price),
        quantity: parseInt(manualItem.quantity),
        fromMenu: false
      }
    ]);

    setManualItem({ name: '', quantity: 1, price: '' });
    setShowManualEntry(false);
  };

  // Update quantity
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }

    setSelectedItems(
      selectedItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  // Remove item
  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemId));
  };

  // Calculate total
  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Handle bill generation
  const handleGenerateBill = async () => {
    if (selectedItems.length === 0) {
      alert('Please add at least one item to generate bill');
      return;
    }

    if (!selectedTable) {
      alert('Please select a table');
      return;
    }

    setGenerating(true);

    try {
      // Prepare order data
      const orderData = {
        items: selectedItems,
        customerName,
        tableId: selectedTable,
        tableName: tables.find(t => t.id === selectedTable)?.name || 'Manual Order',
        total: calculateTotal(),
        isManualOrder: true
      };

      await onGenerateBill(orderData);
      onClose();
    } catch (error) {
      console.error('Error generating manual bill:', error);
      alert('Failed to generate bill. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-orange-500" />
            Create Manual Bill
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" disabled={generating}>
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer & Table Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="Enter customer name"
                disabled={generating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Table *
              </label>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                required
                disabled={generating}
              >
                <option value="">Select Table</option>
                {tables.map(table => (
                  <option key={table.id} value={table.id}>
                    {table.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Add Items Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <h3 className="font-bold text-gray-800 mb-3">Add Items</h3>

            {/* Search from Menu */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search from Menu
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Search menu items..."
                  disabled={generating}
                />
              </div>

              {/* Search Results */}
              {searchTerm && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredMenu.length > 0 ? (
                    filteredMenu.map(item => {
                      const hasHalf = item.portionOptions?.includes('Half') && item.priceHalf;
                      const hasFull = item.portionOptions?.includes('Full') && item.priceFull;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleMenuItemClick(item)}
                          className="w-full px-4 py-3 hover:bg-gray-50 flex justify-between items-center border-b last:border-b-0 text-left"
                          disabled={generating}
                        >
                          <div>
                            <p className="font-semibold text-gray-800">{item.name}</p>
                            <p className="text-xs text-gray-500">{item.category}</p>
                            <div className="flex gap-2 mt-1">
                              {hasHalf && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  Half: ₹{item.priceHalf}
                                </span>
                              )}
                              {hasFull && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  Full: ₹{item.priceFull}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-orange-600 font-bold">
                            {hasHalf && hasFull ? 'Choose →' : `₹${hasFull ? item.priceFull : item.priceHalf}`}
                          </span>
                        </button>
                      );
                    })
                  ) : (
                    <p className="p-4 text-center text-gray-500">No items found</p>
                  )}
                </div>
              )}
            </div>

            {/* Manual Entry Toggle */}
            <button
              onClick={() => setShowManualEntry(!showManualEntry)}
              className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
              disabled={generating}
            >
              <Plus className="w-4 h-4" />
              {showManualEntry ? 'Hide Manual Entry' : 'Add Item Manually'}
            </button>

            {/* Manual Item Entry */}
            {showManualEntry && (
              <div className="mt-3 bg-blue-50 p-4 rounded-lg space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Name
                  </label>
                  <input
                    type="text"
                    value={manualItem.name}
                    onChange={(e) => setManualItem({ ...manualItem, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Special Dish"
                    disabled={generating}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={manualItem.quantity}
                      onChange={(e) => setManualItem({ ...manualItem, quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      disabled={generating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manualItem.price}
                      onChange={(e) => setManualItem({ ...manualItem, price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      disabled={generating}
                    />
                  </div>
                </div>

                <button
                  onClick={addManualItem}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
                  disabled={generating}
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            )}
          </div>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-bold text-gray-800">Selected Items ({selectedItems.length})</h3>
              </div>

              <div className="divide-y">
                {selectedItems.map(item => (
                  <div key={item.id} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{item.name}</p>
                      {item.fromMenu && (
                        <p className="text-xs text-blue-600">From Menu</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-gray-200 rounded-lg hover:bg-gray-300 flex items-center justify-center"
                          disabled={generating}
                        >
                          -
                        </button>
                        <span className="w-12 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-gray-200 rounded-lg hover:bg-gray-300 flex items-center justify-center"
                          disabled={generating}
                        >
                          +
                        </button>
                      </div>

                      <span className="w-24 text-right font-bold text-orange-600">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </span>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-500 hover:text-red-700"
                        disabled={generating}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 px-4 py-4 border-t-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-800">Subtotal:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    ₹{calculateTotal().toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  (GST and other charges will be calculated in final bill)
                </p>
              </div>
            </div>
          )}

          {/* Generate Bill Button */}
          <button
            onClick={handleGenerateBill}
            disabled={generating || selectedItems.length === 0 || !selectedTable}
            className="w-full bg-green-500 text-white py-4 rounded-lg font-bold text-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
          >
            {generating ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                Generating Bill...
              </>
            ) : (
              <>
                <ShoppingCart className="w-6 h-6" />
                Generate Bill - ₹{calculateTotal().toFixed(2)}
              </>
            )}
          </button>
        </div>
      </div>

      {/* ✅ PORTION SELECTION MODAL */}
      {showPortionModal && selectedMenuItem && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Select Portion Size
            </h3>
            <p className="text-gray-600 mb-6">{selectedMenuItem.name}</p>
            
            <div className="space-y-3">
              {selectedMenuItem.portionOptions?.includes('Half') && selectedMenuItem.priceHalf && (
                <button
                  onClick={() => addItemFromMenu(selectedMenuItem, selectedMenuItem.priceHalf, 'Half')}
                  className="w-full bg-blue-500 text-white py-4 rounded-lg hover:bg-blue-600 transition flex items-center justify-between px-6"
                  disabled={generating}
                >
                  <span className="font-semibold">Half Portion</span>
                  <span className="text-xl font-bold">₹{selectedMenuItem.priceHalf}</span>
                </button>
              )}
              
              {selectedMenuItem.portionOptions?.includes('Full') && selectedMenuItem.priceFull && (
                <button
                  onClick={() => addItemFromMenu(selectedMenuItem, selectedMenuItem.priceFull, 'Full')}
                  className="w-full bg-green-500 text-white py-4 rounded-lg hover:bg-green-600 transition flex items-center justify-between px-6"
                  disabled={generating}
                >
                  <span className="font-semibold">Full Portion</span>
                  <span className="text-xl font-bold">₹{selectedMenuItem.priceFull}</span>
                </button>
              )}
            </div>

            <button
              onClick={() => {
                setShowPortionModal(false);
                setSelectedMenuItem(null);
              }}
              className="w-full mt-4 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition"
              disabled={generating}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualBillingModal;